import { useState } from "react";
import { VyberKovaniaDialog } from "@/components/dielec/VyberKovaniaDialog";
import { useUpdateDielec } from "@/api/hooks/useDielce";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { jePredvolenyNazovKovania, predvolenyNazovKovania } from "@/lib/kategorie";
import { menoKovania } from "@/lib/format";
import type { Dielec, Material } from "@/api/schemas";

export interface KovanieChangeDialogProps {
  zakazkaId: string;
  dielec: Dielec;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Zmena kovania na existujúcom dielci — výber iného kovania z cenníka
 * v okne výberu (KOLO 202: klik označí, **Zmeniť** alebo dvojklik
 * potvrdí; dovtedy menil prvý klik). Ak mal dielec názov odvodený
 * z kategórie (alebo žiadny), prepíše sa podľa nového kovania; vlastný
 * názov ostáva.
 */
export function KovanieChangeDialog({
  zakazkaId,
  dielec,
  open,
  onOpenChange,
}: KovanieChangeDialogProps): JSX.Element {
  const updateMutation = useUpdateDielec(zakazkaId);
  const [busy, setBusy] = useState(false);

  async function handlePick(material: Material): Promise<void> {
    // výber aktuálneho kovania = žiadna zmena, len zavri
    if (material.id === dielec.material_id_direct) {
      onOpenChange(false);
      return;
    }
    const data: Record<string, string | null> = { material_id_direct: material.id };
    // Názov, ktorý dala appka (alias, kategória, pravý názov), sa prepíše
    // na predvolený nového kovania — vlastný názov od usera ostane
    const novy = predvolenyNazovKovania(material);
    if (novy && jePredvolenyNazovKovania(dielec.nazov_strojovy, dielec.material_direct)) {
      data.nazov_strojovy = novy;
    }
    setBusy(true);
    try {
      await updateMutation.mutateAsync({ id: dielec.id, data });
      toast({
        title: "Kovanie zmenené",
        description: menoKovania(material).hlavne,
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba pri zmene kovania",
        description: extractErrorMessage(err),
      });
    } finally {
      setBusy(false);
    }
  }

  const aktualne = dielec.material_direct ? menoKovania(dielec.material_direct) : null;
  return (
    <VyberKovaniaDialog
      open={open}
      onClose={() => onOpenChange(false)}
      onSelect={(m) => void handlePick(m)}
      nadpis={`Zmeniť kovanie — ${dielec.kod}`}
      popis={
        aktualne ? (
          <>
            Aktuálne: <span className="font-medium text-foreground">{aktualne.hlavne}</span>
            {aktualne.pod ? ` (${aktualne.pod})` : ""}
          </>
        ) : undefined
      }
      potvrdText={busy ? "Mením…" : "Zmeniť"}
      busy={busy}
      // Okno začne v kategórii terajšieho kovania; samo sa neponúka
      kategoriaPodla={dielec.material_id_direct}
      vynechat={dielec.material_id_direct ? [dielec.material_id_direct] : undefined}
    />
  );
}
